import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Leaf, MapPin, Plus, Search, Star,
} from "lucide-react";

import { ClimateProjectFormDialog } from "@/components/ClimateProjectFormDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useClimateProjects } from "@/hooks/useClimateProjects";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { ClimateProjectRow, ClimateProjectStatus, ClimateProjectType } from "@/lib/types";

/** /climate-projects — CEO-authored programme of work. */
export default function ClimateProjects() {
  const { data: user } = useCurrentUser();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ClimateProjectStatus | "">("");
  const [type,   setType]   = useState<ClimateProjectType | "">("");
  const [page,   setPage]   = useState(1);
  const [opening, setOpening] = useState(false);

  const list = useClimateProjects({
    search:       search || undefined,
    status:       status || undefined,
    project_type: type || undefined,
    page,
  });

  const canCreate = !!user?.is_ceo_level;
  const data = list.data;
  const pageCount = data ? Math.max(1, Math.ceil(data.count / 20)) : 1;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            <Leaf className="h-3.5 w-3.5" />
            Climate projects
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Climate projects &amp; actions</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Programme work — projects and actions tracked across the climate portfolio.
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setOpening(true)} className="self-start sm:self-auto">
            <Plus className="mr-2 h-4 w-4" />New project
          </Button>
        )}
      </header>

      {/* Filter bar */}
      <Card className="border-border/60 shadow-soft">
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <div className="relative min-w-[14rem] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search title, summary, location…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="h-9 pl-9 pr-3"
            />
          </div>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value as ClimateProjectStatus | ""); setPage(1); }}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm font-medium"
          >
            <option value="">All statuses</option>
            <option value="pipeline">Pipeline</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </select>
          <select
            value={type}
            onChange={(e) => { setType(e.target.value as ClimateProjectType | ""); setPage(1); }}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm font-medium"
          >
            <option value="">All types</option>
            <option value="project">Projects</option>
            <option value="action">Actions</option>
          </select>
          <span className="ml-auto text-xs text-muted-foreground">
            {data?.count ?? 0} project{data?.count === 1 ? "" : "s"}
          </span>
        </CardContent>
      </Card>

      {/* Card grid */}
      {list.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-64 w-full" />)}
        </div>
      ) : data && data.results.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.results.map((p) => <ProjectCard key={p.id} project={p} />)}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Leaf className="h-5 w-5 text-muted-foreground" />
            </div>
            <h3 className="text-sm font-semibold">
              {search || status || type ? "No matches" : "No projects yet"}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {canCreate
                ? "Click “New project” to add the first one."
                : "A CEO will add them here once they're set up."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex items-center justify-between gap-4">
          <span className="text-xs text-muted-foreground">Page {page} / {pageCount}</span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={!data?.previous}
              onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="mr-1 h-3.5 w-3.5" />Prev
            </Button>
            <Button variant="ghost" size="sm" disabled={!data?.next}
              onClick={() => setPage((p) => p + 1)}>
              Next<ChevronRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      <ClimateProjectFormDialog open={opening} onOpenChange={setOpening} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function ProjectCard({ project: p }: { project: ClimateProjectRow }) {
  return (
    <Link to={`/climate-projects/${p.id}`} className="group block">
      <Card className="h-full overflow-hidden border-border/60 shadow-soft transition hover:-translate-y-0.5 hover:shadow-soft-lg">
        {/* Cover */}
        <div className="relative aspect-[16/9] bg-gradient-to-br from-primary/15 to-emerald-700/10">
          {p.image_url ? (
            <img src={p.image_url} alt="" loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Leaf className="h-10 w-10 text-primary/40" />
            </div>
          )}
          <div className="absolute left-2.5 top-2.5">
            <StatusPill status={p.status} label={p.status_display} />
          </div>
          {p.is_featured && (
            <div className="absolute right-2.5 top-2.5 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-300/60 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-500/30">
              <Star className="mr-0.5 inline h-3 w-3 fill-amber-400" />Featured
            </div>
          )}
          {!p.is_published && (
            <div className="absolute bottom-2 right-2 rounded-full bg-slate-900/70 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur">
              Draft
            </div>
          )}
        </div>

        <CardContent className="p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {p.initiative} · {p.project_type_display}
          </div>
          <h3 className="mt-1 line-clamp-2 text-sm font-semibold group-hover:text-primary">
            {p.title}
          </h3>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{p.summary}</p>

          <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
            {p.location && (
              <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{p.location}</span>
            )}
            {p.start_date && <span>· {formatDate(p.start_date)}{p.end_date ? ` → ${formatDate(p.end_date)}` : ""}</span>}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export function StatusPill({ status, label }: { status: ClimateProjectStatus; label: string }) {
  const cls =
    status === "active"     ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30"
  : status === "completed"  ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30"
                            : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30";
  return <Badge variant="outline" className={cn("border font-medium", cls)}>{label}</Badge>;
}
